import React, { useState, useEffect, useRef } from 'react';
import PageHeader from './PageHeader';
import { API } from '../apiClient';

async function api(path, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    ...(options.body && !(options.body instanceof FormData) ? {'Content-Type': 'application/json'} : {}),
    ...(options.headers || {}),
    ...(token ? {Authorization: `Bearer ${token}`} : {})
  };
  const res = await fetch(`${API}${path}`, {...options, headers});
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

function formatMessageTime(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('pl-PL');
}

export default function Agent() {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadMessages();
    loadSummary();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages.length]);

  async function loadSummary() {
    try {
      const data = await api('/agent/summary');
      setSummary(data);
    } catch (err) {
      console.error('Failed to load summary:', err);
    }
  }

  async function loadMessages() {
    try {
      setError('');
      const data = await api('/agent/messages');
      setMessages(data);
    } catch (err) {
      console.error('Failed to load messages:', err);
      setError(err.message || 'Nie udało się pobrać historii agenta.');
    }
  }

  async function sendMessage(e) {
    e.preventDefault();
    const content = newMessage.trim();
    if (!content || isProcessing) return;

    setIsProcessing(true);
    setError('');
    try {
      const res = await api('/agent/messages', {
        method: 'POST',
        body: JSON.stringify({ content })
      });
      setNewMessage('');
      if (res.messages) {
        setMessages(res.messages);
      } else {
        await loadMessages();
      }
      await loadSummary();
    } catch (err) {
      console.error('Failed to send message:', err);
      setError(err.message || 'Nie udało się wysłać wiadomości do agenta.');
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <section>
      <PageHeader
        icon="🤖"
        title="Panel agenta"
        description="Miejsce na prośby do agenta. Agent sprawdza rezerwacje, sprzątania, zakupy, Booking i email. Jeśli ma odpisać gościowi, a nie zna pewnej odpowiedzi, najpierw pyta Michała na WhatsApp."
        helpKey="agent"
        stats={[
          { label: 'Wiadomości', value: messages.length, tone: 'info' },
          { label: 'Twoje', value: messages.filter(m => m.role === 'user').length, tone: 'primary' },
          { label: 'Agenta', value: messages.filter(m => m.role === 'assistant').length, tone: 'success' },
        ]}
      />
      <div className="card agent-brief-card">
        <h2>Stan agenta</h2>
        <p className="hint">{summary?.message || 'Ładuję aktualne podsumowanie portalu…'}</p>
        {summary?.next_step && <p className="integration-note">{summary.next_step}</p>}
        <ul className="integration-checklist">
          <li><strong>Historia jest trwała:</strong> wiadomości są zapisywane w bazie portalu, nie tylko w przeglądarce.</li>
          <li><strong>Bez zgadywania:</strong> agent odpowiada na podstawie danych portalu i zaznacza, co wymaga decyzji Michała.</li>
          <li><strong>Operacyjnie:</strong> używaj panelu do zadań typu „sprawdź rezerwację”, „co z Marią?”, „czy są błędy importu?”.</li>
        </ul>
      </div>

      <div className="card agent-chat-card">
        <div className="agent-chat-head">
          <div>
            <h2>Historia rozmowy</h2>
            <p className="hint">Ostatnie 200 wiadomości. Najnowsze są na dole.</p>
          </div>
          <button type="button" className="secondary" onClick={loadMessages}>Odśwież</button>
        </div>
        {error && <div className="error">{error}</div>}
        <div className="agent-messages" aria-live="polite">
          {messages.length === 0 ? (
            <p className="hint">Brak wiadomości — napisz pierwsze zadanie poniżej.</p>
          ) : (
            messages.map(msg => (
              <div key={msg.id} className={`agent-message ${msg.role}`}>
                <div className="agent-message-head">
                  <strong>{msg.role === 'user' ? (msg.author_name || 'Ty') : 'Agent'}</strong>
                  <small>{formatMessageTime(msg.timestamp)}</small>
                </div>
                <p>{msg.content}</p>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      <form className="card form agent-compose" onSubmit={sendMessage}>
        <h2>Wyślij wiadomość do agenta</h2>
        <p className="muted agent-compose-hint">
          Napisz normalnie, np. <em>„sprawdź czy mieszkanie jest gotowe na jutro”</em> albo <em>„co z zakupami Marii?”</em>. Agent odpowiada tutaj, a jeśli nie wie — pyta Michała na WhatsApp.
        </p>
        <div className="agent-compose-body">
          <textarea
            className="agent-compose-input"
            value={newMessage}
            onChange={e => setNewMessage(e.target.value)}
            placeholder="Opisz zadanie dla agenta..."
            rows={4}
            disabled={isProcessing}
            aria-label="Wiadomość do agenta"
          />
          <div className="agent-compose-actions">
            <span className="agent-compose-count">
              {newMessage.length} znaków
            </span>
            <button type="submit" className="primary agent-compose-send" disabled={isProcessing || !newMessage.trim()}>
              {isProcessing ? '⏳ Przetwarzanie...' : '✉️ Wyślij do agenta'}
            </button>
          </div>
        </div>
      </form>
    </section>
  );
}